import React, { useCallback, useEffect } from "react";

import { Wizard, WizardProps } from "@app/component/wizard";
import CardType from "@app/enum/dashboard/CardType";
import CardConfiguration from "@app/types/dashboard/CardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "./AddCardWizard.module.scss";
import AddCardWizardState from "./AddCardWizardState";
import steps from "./steps";

interface Props extends Pick<WizardProps<AddCardWizardState>, "onHide"> {
  onAddCard?: (type: CardType, configuration: CardConfiguration<any, any, any>) => void;
  scope: Scope;
}

const AddCardWizard: React.FC<Props> = ({ onAddCard, onHide, scope }) => {
  const analytics = useExtendedAnalytics("Add new chart", useAnalytics());

  useEffect(() => {
    analytics.initiated();
  }, [analytics]);

  const handleHide = useCallback(
    (finished: boolean) => {
      // we log "completed" in steps, not here
      if (!finished) {
        analytics.cancelled();
      }
      onHide(finished);
    },
    [analytics, onHide]
  );

  const handleFinish = useCallback(
    (state: AddCardWizardState) => {
      analytics.completed("", { cardType: state.cardType!, configuration: state.configuration });
      onAddCard?.(state.cardType!, state.configuration!);
    },
    [onAddCard, analytics]
  );

  return (
    <AnalyticsContext.Provider value={analytics}>
      <Wizard
        className={styles.container}
        initialState={{ scope }}
        onFinish={handleFinish}
        onHide={handleHide}
        steps={steps}
        width="70rem"
      />
    </AnalyticsContext.Provider>
  );
};

export default AddCardWizard;
